/**
 * @voltxampere/schema — .vxa format constants
 *
 * Per SPECS §2.2, §2.3, §2.6, §2.7. A `.vxa` file is a standard ZIP
 * archive (PK\x03\x04 magic) containing a manifest + the circuit JSON
 * and optional asset / model / plugin directories.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * ZIP archive magic bytes. Every valid `.vxa` begins with these four
 * bytes because a `.vxa` is a ZIP with extra structural rules.
 *
 * The sentinel is typed `Readonly<Uint8Array>` so the compiler refuses
 * index-write mutations at call sites. (JavaScript does not allow
 * `Object.freeze` on a typed-array view at runtime, so we rely on the
 * type system alone here.)
 */
export const VXA_MAGIC_BYTES = new Uint8Array([0x50, 0x4b, 0x03, 0x04]);
/**
 * The MIME type file-type sniffers should treat a `.vxa` as, per SPECS §2.2.
 */
export const VXA_MIME_TYPE = "application/vnd.vxa+zip";
/**
 * Canonical entry filenames inside the ZIP, per SPECS §2.3.
 */
export const MANIFEST_FILENAME = "manifest.json";
export const CIRCUIT_FILENAME = "circuit.json";
export const THUMBNAIL_FILENAME = "thumbnail.png";
export const README_FILENAME = "README.md";
/**
 * Directory prefixes inside the ZIP for optional resources.
 */
export const ASSETS_DIR_PREFIX = "assets/";
export const MODELS_DIR_PREFIX = "models/";
export const PLUGINS_DIR_PREFIX = "plugins/";
/**
 * Format vs. schema versions evolve independently (SPECS §2.7). We ship
 * the following pair at the end of Sprint 2:
 *
 *   - `CURRENT_FORMAT_VERSION` — ZIP layout + manifest schema version.
 *     SPECS §2.4 uses "1.0.0" in the manifest example.
 *   - `CURRENT_VXA_VERSION`    — the VoltXAmpere release series that
 *     produced this writer. SPECS §2.4 uses "3.0.0".
 */
export const CURRENT_FORMAT_VERSION = "1.0.0";
export const CURRENT_VXA_VERSION = "3.0.0";
/**
 * Default DEFLATE compression level for JSON entries (SPECS §2.6).
 * Large already-compressed assets (PNG, WebP, MP4) should be stored
 * with level 0 / STORE to avoid wasting CPU on incompressible data.
 */
export const DEFLATE_LEVEL_DEFAULT = 6;
export const DEFLATE_LEVEL_STORE = 0;
/**
 * Hash / checksum algorithm identifiers written into the manifest.
 */
export const CHECKSUM_ALGORITHM = "sha256";
/**
 * Friendly tag prepended to manifest checksum strings so they are
 * self-describing: `sha256-<hex>` per SPECS §2.4.
 */
export const CHECKSUM_PREFIX = `${CHECKSUM_ALGORITHM}-`;
/**
 * URL of the manifest JSON Schema — aspirational; the artifact is not
 * yet hosted. Included to match SPECS §2.4 example. Writers emit this
 * field; readers do not require it to resolve (URL fetches are never
 * performed during `.vxa` open).
 */
export const MANIFEST_SCHEMA_URL = "https://voltxampere.com/schema/manifest-v1.json";
//# sourceMappingURL=constants.js.map